import * as React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, FileText, CheckCircle2, XCircle, Search, ExternalLink, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { apiConfig } from "@/config/api";
import { apiFetch } from "@/lib/apiFetch";

interface NriRequestItem {
  _id: string;
  callsign: string;
  nri_lama: string;
  evidence_s3_key?: string;
  notes?: string;
  status: "pending" | "approved" | "rejected";
  createdAt: string;
  reviewed_at?: string;
  admin_notes?: string;
  user?: {
    _id: string;
    callsign: string;
    nama_lengkap: string;
    id_client: string;
    nri?: string;
    iar?: string;
    masa_laku_kta?: string;
  };
  reviewed_by?: {
    _id: string;
    callsign: string;
    nama_lengkap: string;
  };
}

interface NriConflictResult {
  conflict: boolean;
  callsign?: string;
  nama_lengkap?: string;
}

export function NriRequestsPage() {
  const [activeTab, setActiveTab] = React.useState<"pending" | "approved" | "rejected">("pending");
  const [requests, setRequests] = React.useState<NriRequestItem[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [nextCursor, setNextCursor] = React.useState<string | null>(null);
  const [hasNext, setHasNext] = React.useState(false);

  const [selectedRequest, setSelectedRequest] = React.useState<NriRequestItem | null>(null);
  const [sheetOpen, setSheetOpen] = React.useState(false);
  const [actionLoading, setActionLoading] = React.useState(false);
  const [adminNotes, setAdminNotes] = React.useState("");
  const [validationResult, setValidationResult] = React.useState<NriConflictResult | null>(null);
  const [validating, setValidating] = React.useState(false);

  const fetchRequests = React.useCallback(async (status: string, cursor?: string) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set("status", status);
      params.set("limit", "20");
      if (cursor) params.set("cursor", cursor);

      const res = await apiFetch(`${apiConfig.baseURL}/api/nri-requests?${params.toString()}`);
      if (!res.ok) {
        if (res.status === 401) {
          setError("Sesi telah berakhir. Silakan login kembali.");
          return;
        }
        throw new Error("Failed to fetch");
      }

      const data = await res.json();
      setRequests(data.data || []);
      setNextCursor(data.nextCursor);
      setHasNext(data.hasNext);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Gagal memuat data");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchRequests(activeTab);
  }, [activeTab, fetchRequests]);

  const openSheet = (request: NriRequestItem) => {
    setSelectedRequest(request);
    setAdminNotes("");
    setValidationResult(null);
    setSheetOpen(true);
  };

  const handleValidate = async () => {
    if (!selectedRequest?.user?._id) return;
    setValidating(true);
    setValidationResult(null);
    try {
      const res = await apiFetch(
        `${apiConfig.baseURL}/api/nri-requests/validate?nri_lama=${selectedRequest.nri_lama}&request_user_id=${selectedRequest.user._id}`
      );
      const data = await res.json();
      setValidationResult(data);
    } catch (err) {
      toast.error("Gagal validasi NRI");
    } finally {
      setValidating(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedRequest) return;
    setActionLoading(true);
    try {
      const body: any = {};
      if (adminNotes.trim()) body.admin_notes = adminNotes.trim();

      const res = await apiFetch(
        `${apiConfig.baseURL}/api/nri-requests/${selectedRequest._id}/approve`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }
      );
      const data = await res.json();

      if (!data.ok) {
        toast.error(data.message || "Gagal menyetujui");
        return;
      }

      toast.success("NRI berhasil disetujui");
      setSheetOpen(false);
      fetchRequests(activeTab);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Gagal menyetujui");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedRequest) return;
    setActionLoading(true);
    try {
      const body: any = {};
      if (adminNotes.trim()) body.admin_notes = adminNotes.trim();

      await apiFetch(
        `${apiConfig.baseURL}/api/nri-requests/${selectedRequest._id}/reject`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }
      );

      toast.success("Permintaan ditolak");
      setSheetOpen(false);
      fetchRequests(activeTab);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Gagal menolak");
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("id-ID", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const statusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return <Badge variant="secondary" className="gap-1"><Loader2 className="h-3 w-3 animate-spin" />Pending</Badge>;
      case "approved":
        return <Badge variant="default" className="gap-1 bg-green-600"><CheckCircle2 className="h-3 w-3" />Disetujui</Badge>;
      case "rejected":
        return <Badge variant="destructive" className="gap-1"><XCircle className="h-3 w-3" />Ditolak</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Permintaan Klaim NRI</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Review dan proses permintaan klaim NRI dari anggota
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as typeof activeTab)}>
        <TabsList>
          <TabsTrigger value="pending">Pending</TabsTrigger>
          <TabsTrigger value="approved">Disetujui</TabsTrigger>
          <TabsTrigger value="rejected">Ditolak</TabsTrigger>
        </TabsList>
      </Tabs>

      {error && (
        <Card className="border-destructive">
          <CardContent className="p-4 text-destructive text-sm">{error}</CardContent>
        </Card>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : requests.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            <FileText className="h-8 w-8 mx-auto mb-2" />
            <p>Tidak ada permintaan {activeTab === "pending" ? "pending" : activeTab === "approved" ? "disetujui" : "ditolak"}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {requests.map((req) => (
            <Card
              key={req._id}
              className="cursor-pointer hover:bg-muted/30 transition-colors"
              onClick={() => openSheet(req)}
            >
              <CardContent className="p-4 flex items-center justify-between">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">{req.user?.nama_lengkap || "-"}</span>
                    <span className="text-xs font-mono text-muted-foreground">{req.callsign}</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <span>
                      <span className="text-muted-foreground">NRI Lama: </span>
                      <span className="font-mono">{req.nri_lama}</span>
                    </span>
                    {req.user?.nri && (
                      <span>
                        <span className="text-muted-foreground">NRI Saat Ini: </span>
                        <span className="font-mono">{req.user.nri}</span>
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground">{formatDate(req.createdAt)}</div>
                </div>
                <div>{statusBadge(req.status)}</div>
              </CardContent>
            </Card>
          ))}

          {hasNext && (
            <div className="text-center pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchRequests(activeTab, nextCursor!)}
                disabled={loading}
              >
                Muat lebih banyak
              </Button>
            </div>
          )}
        </div>
      )}

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="w-[500px] sm:w-[600px] sm:max-w-none overflow-y-auto">
          {selectedRequest && (
            <>
              <SheetHeader className="px-6 pt-6">
                <SheetTitle>Detail Permintaan Klaim NRI</SheetTitle>
                <SheetDescription>
                  Review permintaan dari {selectedRequest.user?.nama_lengkap} ({selectedRequest.callsign})
                </SheetDescription>
              </SheetHeader>

              <div className="px-6 py-4 space-y-4">
                <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold">{selectedRequest.user?.nama_lengkap}</span>
                    {statusBadge(selectedRequest.status)}
                  </div>
                  <div className="text-sm space-y-1">
                    <div><span className="text-muted-foreground">Callsign:</span> <span className="font-mono">{selectedRequest.callsign}</span></div>
                    <div><span className="text-muted-foreground">ID Klien:</span> <span className="font-mono">{selectedRequest.user?.id_client || "-"}</span></div>
                    <div><span className="text-muted-foreground">IAR:</span> <span className="font-mono">{selectedRequest.user?.iar || "-"}</span></div>
                    <div><span className="text-muted-foreground">NRI Saat Ini:</span> <span className="font-mono">{selectedRequest.user?.nri || "-"}</span></div>
                    <div><span className="text-muted-foreground">Masa Laku KTA:</span> {selectedRequest.user?.masa_laku_kta || "-"}</div>
                  </div>
                </div>

                <div className="space-y-2">
                  <h4 className="font-semibold text-sm">NRI yang Diajukan</h4>
                  <div className="bg-muted/30 rounded-lg p-3">
                    <span className="font-mono text-lg font-semibold">{selectedRequest.nri_lama}</span>
                  </div>
                </div>

                {selectedRequest.notes && (
                  <div className="space-y-2">
                    <h4 className="font-semibold text-sm">Keterangan</h4>
                    <div className="bg-muted/30 rounded-lg p-3 text-sm whitespace-pre-wrap">
                      {selectedRequest.notes}
                    </div>
                  </div>
                )}

                {selectedRequest.evidence_s3_key && (
                  <div className="space-y-2">
                    <h4 className="font-semibold text-sm">Bukti</h4>
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-2"
                      asChild
                    >
                      <a
                        href={`${apiConfig.baseURL}/api/nri-requests/evidence/${selectedRequest.evidence_s3_key}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <ExternalLink className="h-4 w-4" />
                        Lihat Bukti
                      </a>
                    </Button>
                  </div>
                )}

                {selectedRequest.reviewed_at && (
                  <div className="space-y-2">
                    <h4 className="font-semibold text-sm">Review</h4>
                    <div className="bg-muted/30 rounded-lg p-3 text-sm space-y-1">
                      <div>
                        <span className="text-muted-foreground">Direview oleh: </span>
                        {selectedRequest.reviewed_by?.nama_lengkap || "-"} ({selectedRequest.reviewed_by?.callsign || "-"})
                      </div>
                      <div>
                        <span className="text-muted-foreground">Tanggal: </span>
                        {formatDate(selectedRequest.reviewed_at)}
                      </div>
                      {selectedRequest.admin_notes && (
                        <div>
                          <span className="text-muted-foreground">Catatan: </span>
                          {selectedRequest.admin_notes}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {selectedRequest.status === "pending" && (
                  <>
                    <div className="space-y-2">
                      <h4 className="font-semibold text-sm">Validasi NRI</h4>
                      {validationResult ? (
                        validationResult.conflict ? (
                          <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3 flex items-start gap-2">
                            <ShieldAlert className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
                            <div className="text-sm">
                              <p className="font-medium text-destructive">Bentrok!</p>
                              <p>
                                NRI sudah digunakan oleh {validationResult.nama_lengkap} ({validationResult.callsign})
                              </p>
                            </div>
                          </div>
                        ) : (
                          <div className="bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg p-3 flex items-start gap-2">
                            <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0 mt-0.5" />
                            <div className="text-sm">
                              <p className="font-medium text-green-600">NRI tersedia</p>
                              <p className="text-muted-foreground">Tidak bentrok dengan pengguna lain</p>
                            </div>
                          </div>
                        )
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleValidate}
                          disabled={validating}
                          className="gap-2"
                        >
                          {validating ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Search className="h-4 w-4" />
                          )}
                          Validasi
                        </Button>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="admin-notes">Catatan Admin</Label>
                      <Textarea
                        id="admin-notes"
                        placeholder="Catatan untuk approval/rejection (opsional)"
                        value={adminNotes}
                        onChange={(e) => setAdminNotes(e.target.value)}
                        rows={2}
                      />
                    </div>
                  </>
                )}
              </div>

              {selectedRequest.status === "pending" && (
                <SheetFooter className="px-6 pb-6 gap-2">
                  <Button
                    variant="destructive"
                    onClick={handleReject}
                    disabled={actionLoading}
                    className="gap-2"
                  >
                    {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
                    Tolak
                  </Button>
                  <Button
                    onClick={handleApprove}
                    disabled={actionLoading}
                    className="gap-2"
                  >
                    {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                    Setujui
                  </Button>
                </SheetFooter>
              )}
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
